import { Button, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRendererProps } from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { useStore } from '../../hooks/use-store';

const PHONE_SAP_LENGTH = 6;

export interface SapUpgradeVersion {
  sapId: string;
  csVersion: string;
}

interface RowCellRendererProps extends GridRendererProps<SapUpgradeVersion> {
  field: keyof SapUpgradeVersion;
}

const AddUsersRowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const store = useStore().manageUpgradeVersionsStore;
  const { loaders, editObjects } = store;
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Focus on the first row's SAP ID when modal opens, or the last row's SAP ID when a new row is added
    if (field === 'sapId' && (index === 0 || index === editObjects.length - 1)) {
      ref.current?.focus();
    }
  }, [field, index, editObjects.length]);

  const changeValue = (fieldToChange: keyof SapUpgradeVersion, value: string): void => {
    if (fieldToChange === 'sapId' && !/^\d*$/.test(value)) return;
    runInAction(() => {
      row[fieldToChange] = value;
      store.validated = false;
      store.editObjects[index].errors[fieldToChange] = undefined;
    });
  };

  return (
    <FieldGroupInCell
      htmlFor={`table-cell-text-box_${field}`}
      errorText={store.editObjects[index].errors[field]}
    >
      <TextBox
        ref={ref}
        id={`table-cell-text-box_${field}`}
        data-testid={`table-cell-text-box_${field}`}
        placeholder={field === 'sapId' ? 'Add SAP' : field === 'csVersion' ? 'Upgrade Version' : ''}
        value={row[field] || ''}
        maxLength={field === 'sapId' ? PHONE_SAP_LENGTH : undefined}
        onChange={e => changeValue(field, e.target.value)}
        disabled={loaders.validateVersions}
      />
    </FieldGroupInCell>
  );
});

export const AddVersionActionsRenderer = observer((props: GridRendererProps<SapUpgradeVersion>) => {
  const { row } = props;
  const store = useStore().manageUpgradeVersionsStore;
  return (
    <Button
      variant='link'
      iconOnly='delete'
      onClick={() => store.deleteRow(row)}
      title='Delete row'
      aria-label='Delete Added Row'
      data-testid={`delete-add-row-${row.sapId}-btn`}
    />
  );
});

export const ModalUpgradeVersionsCustomFooter = observer(() => {
  const store = useStore().manageUpgradeVersionsStore;
  const { editObjects, loaders, sapUpgradeVersionsValid } = store;

  return (
    <>
      <Button
        variant='secondary'
        onClick={() => store.addRow()}
        disabled={loaders.updateVersions || loaders.validateVersions}
      >
        Add Row
      </Button>
      <Button
        variant='secondary'
        disabled={editObjects.length === 0 || loaders.updateVersions}
        onClick={() => void store.validateSapUpgradeVersions()}
      >
        <Loader loading={loaders.validateVersions}>Validate</Loader>
      </Button>
      <Button
        variant='primary'
        disabled={!sapUpgradeVersionsValid || loaders.validateVersions}
        aria-label='Apply Changes'
        onClick={() => void store.updateSapUpgradeVersions()}
      >
        <Loader loading={loaders.updateVersions}>Apply Changes</Loader>
      </Button>
    </>
  );
});

export const addVersionRowCellRenderer =
  (field: keyof SapUpgradeVersion) =>
  (props: GridRendererProps<SapUpgradeVersion>): React.JSX.Element => (
    <AddUsersRowCellRenderer row={props.row} index={props.index} field={field} />
  );
